import { useAsyncEffect } from '@speedlo/hooks'
import { Instance, SnapshotIn } from 'mobx-state-tree'
import React, { Children, createContext, useContext, useState } from 'react'

import { persistModel } from '../helpers/persistModel'
import { RootModel } from '../models/RootModel'
import { useFirebase } from './Firebase'
import { LoaderSpinner } from './LoaderSpinner'

interface TRootModel extends Instance<typeof RootModel> {}
interface TRootModelProps extends SnapshotIn<typeof RootModel> {}

const RootContext = createContext<TRootModel | undefined>(undefined)

interface IProps {
  initialState?: TRootModelProps
  noPersistence?: boolean
}

export const RootProvider: React.SFC<IProps> = ({
  children,
  initialState = {},
  noPersistence = false,
}) => {
  const [root, setRoot] = useState<TRootModel | undefined>(undefined)
  const { auth } = useFirebase()

  useAsyncEffect(async () => {
    const rootModel = RootModel.create(initialState, { auth })
    if (noPersistence !== true) {
      await Promise.all(rootModel.persistedModels.map(persistModel))
    }
    setRoot(rootModel)
  })

  return root ? (
    <RootContext.Provider value={root}>
      {Children.only(children)}
    </RootContext.Provider>
  ) : (
    <LoaderSpinner />
  )
}

export const StoryRootProvider: React.FC = ({ children }) => (
  <RootProvider
    noPersistence={true}
    initialState={{ auth: { roles: ['admin'] } }}
  >
    {children}
  </RootProvider>
)

export const useRoot = () => {
  const root = useContext(RootContext)
  if (!root) {
    throw new Error('RootProvider missing in the tree')
  }
  return root
}
